import * as d3 from "d3";

/**
 * Factory that creates a reactive difference chart for scrollytelling.
 *
 * @param {string} containerId - ID of the container div
 * @param {Array}  yearlyData  - Visualization-ready record-trend entries
 * @param {Array}  dailyData   - Full daily entries (same shape)
 * @returns {Object} API: { showIntro, showBaseline, showDivergence, showRecent, showToday }
 */
export const createDifferenceChart = ({ containerId, yearlyData, dailyData, recentBaselineData = [], unit = "C" }) => {
  const container = d3.select(`#${containerId}`);
  if (container.empty() || !yearlyData.length) return null;

  container.selectAll("*").remove();

  const el = container.node();
  const containerWidth = el.getBoundingClientRect().width || 700;
  const containerHeight = el.getBoundingClientRect().height || window.innerHeight * 0.65;
  const margin = { top: 30, right: 30, bottom: 48, left: 58 };
  const width = containerWidth - margin.left - margin.right;
  const height = containerHeight - margin.top - margin.bottom;

  const svg = container
    .append("svg")
    .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
    .attr("width", "100%")
    .style("overflow", "visible");

  const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

  // Clip path so bars don't overflow during transitions
  g.append("defs").append("clipPath")
    .attr("id", `clip-${containerId}`)
    .append("rect")
    .attr("width", width)
    .attr("height", height + 10)
    .attr("y", -5);

  const chartArea = g.append("g").attr("clip-path", `url(#clip-${containerId})`);

  // Period highlight rect
  const periodRect = chartArea.append("rect")
    .attr("class", "period-rect")
    .attr("y", 0).attr("height", height)
    .style("opacity", 0)
    .style("pointer-events", "none");

  // Scales — domain is set dynamically per view via updateYScale()
  const yearlyValues = yearlyData
    .flatMap(d => [d.anomaly, d.rolling_anomaly])
    .filter(v => v != null);
  const yMin = d3.min(yearlyValues) - 1.5;
  const yMax = d3.max(yearlyValues) + 1.5;

  const xScaleFull = d3.scaleUtc()
    .domain(d3.extent(yearlyData, d => d.date))
    .range([0, width]);

  const lastDate = dailyData[dailyData.length - 1]?.date;
  const recentStart = new Date(lastDate);
  recentStart.setUTCFullYear(recentStart.getUTCFullYear() - 2);
  const recentDaily = dailyData.filter(d => d.date >= recentStart);

  const xScaleRecent = d3.scaleUtc()
    .domain(d3.extent(recentDaily, d => d.date))
    .range([0, width]);

  const _today100 = new Date();
  _today100.setUTCHours(0, 0, 0, 0);
  const _window100Start = new Date(_today100);
  _window100Start.setUTCDate(_window100Start.getUTCDate() - 100);

  const last100Daily = dailyData
    .filter(d => d.actual != null && d.actual_anomaly != null && d.date >= _window100Start && d.date <= _today100);

  const xScale100 = d3.scaleUtc()
    .domain([_window100Start, _today100])
    .range([0, width]);

  // Pre-compute unified y-domain for the three 100-day steps so the axis stays stable.
  // Includes actual, baseline, and recent baseline values.
  const _100DayRecentVals = (() => {
    if (!last100Daily.length) return [];
    const currentYear = _today100.getUTCFullYear();
    const mmddSet = new Set(last100Daily.map(d => d.date.toISOString().slice(5, 10)));
    return recentBaselineData
      .filter(r => mmddSet.has(r.month_day) && r.recent_avg != null)
      .map(r => r.recent_avg);
  })();

  const _100DayYValues = [
    ...last100Daily.flatMap(d => [d.actual, d.baseline_avg]),
    ..._100DayRecentVals,
  ].filter(v => v != null);

  function createRollingSeries(data, valueAccessor, windowSize, key = "value", align = "center") {
    const halfWindow = Math.floor(windowSize / 2);
    return data.map((d, index) => {
      const start = align === "trailing"
        ? Math.max(0, index - windowSize + 1)
        : Math.max(0, index - halfWindow);
      const end = align === "trailing"
        ? index
        : Math.min(data.length - 1, index + halfWindow);
      let sum = 0;
      let count = 0;

      for (let i = start; i <= end; i += 1) {
        const value = valueAccessor(data[i]);
        if (value == null) continue;
        sum += value;
        count += 1;
      }

      return {
        ...d,
        [key]: count ? sum / count : null,
      };
    });
  }

  const recentAnomaly = createRollingSeries(
    recentDaily.filter(d => d.actual_anomaly != null),
    d => d.actual_anomaly,
    15,
    "smoothed_anomaly",
    "center"
  );

  const rollingAnnual = yearlyData.filter(d => d.rolling_anomaly != null);

  const trendStartDate = new Date("1951-01-01T00:00:00Z");
  const trendSeries = rollingAnnual.filter(d => d.date >= trendStartDate);
  let regressionParams = null;
  if (trendSeries.length >= 2) {
    const t0 = trendSeries[0].date.getTime();
    const msPerYear = 1000 * 60 * 60 * 24 * 365.25;
    const n = trendSeries.length;
    const xs = trendSeries.map(d => (d.date.getTime() - t0) / msPerYear);
    const ys = trendSeries.map(d => d.rolling_anomaly);
    const xMean = xs.reduce((s, x) => s + x, 0) / n;
    const yMean = ys.reduce((s, y) => s + y, 0) / n;
    const num = xs.reduce((s, x, i) => s + (x - xMean) * (ys[i] - yMean), 0);
    const den = xs.reduce((s, x) => s + (x - xMean) ** 2, 0);
    regressionParams = {
      slope: num / den,
      intercept: yMean - (num / den) * xMean,
      t0,
      msPerYear,
    };
  }

  let xScale = xScaleFull.copy();

  const yScale = d3.scaleLinear()
    .domain([yMin, yMax])
    .range([height, 0])
    .nice();

  // Axis groups
  const xAxisGroup = g.append("g").attr("class", "x-axis")
    .attr("transform", `translate(0,${height})`);
  const yAxisGroup = g.append("g").attr("class", "y-axis");

  function makeXAxis() {
    return d3.axisBottom(xScale).ticks(7).tickFormat(d3.utcFormat("%Y")).tickSizeOuter(0);
  }
  const yAxisFn = d3.axisLeft(yScale).ticks(6).tickFormat(d => `${d}°${unit}`);

  function styleAxis(sel) {
    sel.select(".domain").attr("stroke", "#3a3530");
    sel.selectAll(".tick line").attr("stroke", "#3a3530");
    sel.selectAll(".tick text").attr("fill", "#a89880").style("font-size", "11px");
  }

  xAxisGroup.call(makeXAxis()).call(styleAxis);
  yAxisGroup.call(yAxisFn).call(styleAxis);

  // Y label
  const yAxisLabel = g.append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", -46).attr("x", -height / 2)
    .attr("text-anchor", "middle")
    .attr("fill", "#a89880")
    .style("font-size", "11px")
    .text(`Temperature (°${unit})`);

  // Baseline curve (1951-1980)
  const baselinePath = chartArea.append("path")
    .attr("fill", "none")
    .attr("stroke", "#8b7355")
    .attr("stroke-width", 1.5)
    .style("opacity", 0);

  // Recent baseline curve (last 20 years)
  const recentBaselinePath = chartArea.append("path")
    .attr("fill", "none")
    .attr("stroke", "#5b9ec9")
    .attr("stroke-width", 1.5)
    .style("opacity", 0);

  // Zero-reference line for anomaly views
  const zeroLinePath = chartArea.append("line")
    .attr("stroke", "#f2ede6")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "4,3")
    .style("opacity", 0);

  // Area chart elements for RECENT YEARS view
  const hotAreaPath = chartArea.append("path")
    .attr("fill", "#ff4d1c")
    .style("opacity", 0);
  const coolAreaPath = chartArea.append("path")
    .attr("fill", "#4a7fa5")
    .style("opacity", 0);
  const actualLinePath = chartArea.append("path")
    .attr("fill", "none")
    .attr("stroke", "#ff6b47")
    .attr("stroke-width", 2)
    .style("opacity", 0);

  // Smooth line for current-year data in ARRIVING EARLIER view
  const currentYearLinePath = chartArea.append("path")
    .attr("fill", "none")
    .attr("stroke", "#ff6b47")
    .attr("stroke-width", 1.5)
    .style("opacity", 0);

  // Annotation group (above bars)
  const annotationGroup = g.append("g").attr("class", "annotations");

  // Tooltip
  const tooltip = container.append("div")
    .style("position", "absolute")
    .style("background", "#1a1917")
    .style("border", "1px solid #3a3530")
    .style("color", "#f2ede6")
    .style("padding", "8px 12px")
    .style("border-radius", "4px")
    .style("font-size", "12px")
    .style("line-height", "1.6")
    .style("pointer-events", "none")
    .style("opacity", 0)
    .style("z-index", 100);

  // ── Scatter dots (monthly actuals) ────────────────────────────────────────

  function renderScatterDots(durationMs = 600) {
    const dots = chartArea.selectAll(".scatter-dot")
      .data(yearlyData.filter(d => d.anomaly != null), d => d.date.toISOString());

    dots.enter().append("circle")
      .attr("class", "scatter-dot")
      .attr("r", 2)
      .attr("cx", d => xScale(d.date))
      .attr("cy", d => yScale(d.anomaly))
      .attr("fill", "#c4a882")
      .style("opacity", 0)
      .transition().duration(durationMs)
      .style("opacity", 0.3);

    dots.transition().duration(durationMs)
      .attr("cx", d => xScale(d.date))
      .attr("cy", d => yScale(d.anomaly))
      .style("opacity", 0.3);

    dots.exit().transition().duration(durationMs).style("opacity", 0).remove();
  }

  function hideScatterDots(durationMs = 400) {
    chartArea.selectAll(".scatter-dot")
      .transition().duration(durationMs)
      .style("opacity", 0)
      .remove();
  }

  // ── Bar rendering ─────────────────────────────────────────────────────────

  function renderBars(data, colorFn, opacityFn, durationMs = 600) {
    const barW = Math.max((width / data.length) * 0.75, 1.5);
    const isYearly = data === yearlyData;
    const fmt = isYearly ? d3.utcFormat("%Y") : d3.utcFormat("%b %d, %Y");

    const bars = chartArea.selectAll(".bar")
      .data(data, d => d.date.toISOString());

    bars.enter().append("rect")
      .attr("class", "bar")
      .attr("rx", 1).attr("ry", 1)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", d => d.actual_anomaly >= 0 ? yScale(d.actual) : yScale(d.baseline_avg))
      .attr("width", barW)
      .attr("height", d => Math.abs(yScale(d.actual) - yScale(d.baseline_avg)))
      .attr("fill", colorFn)
      .style("opacity", 0)
      .on("mouseover", function (event, d) {
        d3.select(this).style("opacity", 1);
        tooltip.style("opacity", 1)
          .html(`
            <div style="font-weight:700;margin-bottom:3px">${fmt(d.date)}</div>
            <div>Actual: <strong>${d.actual?.toFixed(1)}°${unit}</strong></div>
            <div>Baseline avg: ${d.baseline_avg?.toFixed(1)}°${unit}</div>
            <div style="color:${d.actual_anomaly >= 0 ? "#ff6b47" : "#4a90e2"}">
              ${d.actual_anomaly >= 0 ? "▲" : "▼"} ${Math.abs(d.actual_anomaly).toFixed(1)}°${unit}
            </div>
          `)
          .style("left", `${event.offsetX + 16}px`)
          .style("top", `${event.offsetY - 10}px`);
      })
      .on("mouseout", function (event, d) {
        d3.select(this).style("opacity", opacityFn(d));
        tooltip.style("opacity", 0);
      })
      .transition().duration(durationMs)
      .style("opacity", opacityFn);

    bars.transition().duration(durationMs)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", d => d.actual_anomaly >= 0 ? yScale(d.actual) : yScale(d.baseline_avg))
      .attr("width", barW)
      .attr("height", d => Math.abs(yScale(d.actual) - yScale(d.baseline_avg)))
      .attr("fill", colorFn)
      .style("opacity", opacityFn);

    bars.exit().transition().duration(durationMs).style("opacity", 0).remove();
  }

  function hideAreaElements(durationMs = 400) {
    hotAreaPath.transition().duration(durationMs).style("opacity", 0);
    coolAreaPath.transition().duration(durationMs).style("opacity", 0);
    actualLinePath.transition().duration(durationMs).style("opacity", 0);
    currentYearLinePath.transition().duration(durationMs).style("opacity", 0);
    zeroLinePath.transition().duration(durationMs).style("opacity", 0);
    recentBaselinePath.interrupt().style("opacity", 0).attr("d", null);
  }

  // Bars rising from chart floor to absolute temperature value (raw temp view)
  function renderBarsRaw(data, durationMs = 600) {
    const barW = Math.max((width / data.length) * 0.75, 1.5);

    const y = d => yScale(d.actual);
    const h = d => height - yScale(d.actual);

    const bars = chartArea.selectAll(".bar")
      .data(data, d => d.date.toISOString());

    bars.enter().append("rect")
      .attr("class", "bar")
      .attr("rx", 1).attr("ry", 1)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", y)
      .attr("width", barW)
      .attr("height", h)
      .attr("fill", "#c4a882")
      .style("opacity", 0)
      .transition().duration(durationMs)
      .style("opacity", 0.65);

    bars.transition().duration(durationMs)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", y)
      .attr("width", barW)
      .attr("height", h)
      .attr("fill", "#c4a882")
      .style("opacity", 0.65);

    bars.exit().transition().duration(durationMs).style("opacity", 0).remove();
  }

  // Bars showing anomaly delta, centered on zero (difference view)
  function renderBarsDelta(data, durationMs = 700) {
    const barW = Math.max((width / data.length) * 0.75, 1.5);
    const colorFn = d => d.actual_anomaly >= 0 ? "#ff4d1c" : "#4a7fa5";

    const y = d => d.actual_anomaly >= 0 ? yScale(d.actual_anomaly) : yScale(0);
    const h = d => Math.abs(yScale(d.actual_anomaly) - yScale(0));

    const bars = chartArea.selectAll(".bar")
      .data(data, d => d.date.toISOString());

    bars.enter().append("rect")
      .attr("class", "bar")
      .attr("rx", 1).attr("ry", 1)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", y)
      .attr("width", barW)
      .attr("height", h)
      .attr("fill", colorFn)
      .style("opacity", 0)
      .transition().duration(durationMs)
      .style("opacity", 0.85);

    bars.transition().duration(durationMs)
      .attr("x", d => xScale(d.date) - barW / 2)
      .attr("y", y)
      .attr("width", barW)
      .attr("height", h)
      .attr("fill", colorFn)
      .style("opacity", 0.85);

    bars.exit().transition().duration(durationMs).style("opacity", 0).remove();
  }

  function updateYScale(values, durationMs = 600) {
    const filtered = values.filter(v => v != null);
    const [vMin, vMax] = d3.extent(filtered);
    const pad = Math.max((vMax - vMin) * 0.08, 1);
    yScale.domain([vMin - pad, vMax + pad]).nice();
    yAxisGroup.transition().duration(durationMs).call(yAxisFn).call(styleAxis);
  }

  function setYAxisLabel(label) {
    yAxisLabel.text(label);
  }

  function updateBaselineCurve(data, durationMs = 600) {
    const [xMin, xMax] = xScale.domain();
    const lineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.baseline_avg))
      .defined(d => d.baseline_avg != null && d.date >= xMin && d.date <= xMax)
      .curve(d3.curveMonotoneX);
    baselinePath.datum(data).transition().duration(durationMs).attr("d", lineGen);
  }

  function updateXAxis(durationMs = 700) {
    xAxisGroup.transition().duration(durationMs).call(makeXAxis()).call(styleAxis);
  }

  // ── Public API ────────────────────────────────────────────────────────────

  // Helper: draw the backend-computed rolling trend line onto actualLinePath
  function drawRollingAnnualLine(durationMs = 700) {
    const lineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.rolling_anomaly))
      .defined(d => d.rolling_anomaly != null)
      .curve(d3.curveMonotoneX);

    actualLinePath
      .datum(rollingAnnual)
      .attr("stroke", "#c4a882")
      .attr("stroke-width", 2)
      .attr("d", lineGen)
      .transition().duration(durationMs)
      .style("opacity", 0.85);
  }

  function showIntro() {
    xScale = xScaleFull.copy();
    updateXAxis();
    updateYScale(yearlyData.flatMap(d => [d.anomaly, d.rolling_anomaly]).filter(v => v != null));
    setYAxisLabel(`Anomaly vs. 1951–1980 baseline (°${unit})`);
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();
    baselinePath.transition().duration(300).style("opacity", 0);
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hotAreaPath.transition().duration(400).style("opacity", 0);
    coolAreaPath.transition().duration(400).style("opacity", 0);
    currentYearLinePath.transition().duration(400).style("opacity", 0);
    zeroLinePath.transition().duration(400).style("opacity", 0);
    renderScatterDots();
    drawRollingAnnualLine();
  }

  function showBaseline() {
    xScale = xScaleFull.copy();
    updateXAxis();
    updateYScale(yearlyData.flatMap(d => [d.anomaly, d.rolling_anomaly]).filter(v => v != null));
    setYAxisLabel(`Anomaly vs. 1951–1980 baseline (°${unit})`);
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hotAreaPath.transition().duration(400).style("opacity", 0);
    coolAreaPath.transition().duration(400).style("opacity", 0);
    currentYearLinePath.transition().duration(400).style("opacity", 0);
    zeroLinePath.transition().duration(400).style("opacity", 0);
    baselinePath.transition().duration(300).style("opacity", 0);
    recentBaselinePath.interrupt().style("opacity", 0).attr("d", null);
    renderScatterDots();
    drawRollingAnnualLine(400);

    const b1 = new Date("1951-01-01T00:00:00Z");
    const b2 = new Date("1981-01-01T00:00:00Z");
    const x1 = xScaleFull(b1);
    const x2 = xScaleFull(b2);

    periodRect
      .attr("x", x1).attr("width", x2 - x1)
      .attr("fill", "#4a7fa5")
      .transition().duration(600).style("opacity", 0.15);

    annotationGroup.selectAll("*").remove();
    annotationGroup.append("text")
      .attr("x", (x1 + x2) / 2).attr("y", -12)
      .attr("text-anchor", "middle")
      .attr("fill", "#4a7fa5")
      .style("font-size", "11px")
      .style("letter-spacing", "0.04em")
      .text("1951 – 1980 baseline");
  }

  function showWarmingRate() {
    xScale = xScaleFull.copy();
    updateXAxis();
    updateYScale(yearlyData.flatMap(d => [d.anomaly, d.rolling_anomaly]).filter(v => v != null));
    setYAxisLabel(`Anomaly vs. 1951–1980 baseline (°${unit})`);
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hotAreaPath.transition().duration(400).style("opacity", 0);
    coolAreaPath.transition().duration(400).style("opacity", 0);
    currentYearLinePath.transition().duration(400).style("opacity", 0);
    zeroLinePath.transition().duration(400).style("opacity", 0);
    baselinePath.transition().duration(300).style("opacity", 0);
    recentBaselinePath.interrupt().style("opacity", 0).attr("d", null);
    periodRect.transition().duration(400).style("opacity", 0);

    renderScatterDots(400);
    drawRollingAnnualLine(400);
    annotationGroup.selectAll("*").remove();

    const warmingRatePerDecade = yearlyData[0]?.warming_rate_per_decade;
    if (!regressionParams || warmingRatePerDecade == null) return;

    const { slope, intercept, t0, msPerYear } = regressionParams;
    const endDate = rollingAnnual[rollingAnnual.length - 1].date;
    const xStart = (trendStartDate.getTime() - t0) / msPerYear;
    const xEnd = (endDate.getTime() - t0) / msPerYear;
    const yStart = intercept + slope * xStart;
    const yEnd = intercept + slope * xEnd;

    annotationGroup.append("line")
      .attr("x1", xScale(trendStartDate)).attr("x2", xScale(endDate))
      .attr("y1", yScale(yStart)).attr("y2", yScale(yEnd))
      .attr("stroke", "#ff4d1c")
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "6,4")
      .style("opacity", 0)
      .transition().duration(700)
      .style("opacity", 0.75);

    const perDecade = warmingRatePerDecade;
    const sign = perDecade >= 0 ? "+" : "";
    const labelX = xScale(endDate);
    const labelY = yScale(yEnd);
    annotationGroup.append("text")
      .attr("x", labelX - 8)
      .attr("y", labelY - 10)
      .attr("text-anchor", "end")
      .attr("fill", "#ff6b47")
      .style("font-size", "12px")
      .style("font-weight", "700")
      .style("font-family", "Helvetica Neue, Arial, sans-serif")
      .style("opacity", 0)
      .transition().duration(700)
      .style("opacity", 1)
      .on("end", function () { d3.select(this).style("opacity", 1); })
      .text(`${sign}${perDecade.toFixed(2)}°${unit} per decade since 1951`);
  }

  function showDivergence() {
    setYAxisLabel(`Anomaly vs. 1951–1980 baseline (°${unit})`);
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();
    hideScatterDots();
    baselinePath.transition().duration(600).style("opacity", 0.5);

    renderBars(yearlyData,
      d => d.actual_anomaly >= 0 ? "#ff4d1c" : "#4a7fa5",
      () => 0.85
    );
  }

  function showRecent() {
    xScale = xScaleRecent.copy();
    updateXAxis();
    updateYScale([0, ...recentAnomaly.map(d => d.smoothed_anomaly)]);
    setYAxisLabel(`Difference vs. baseline (°${unit})`);
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();
    hideScatterDots();

    // Remove any bars from previous views
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    currentYearLinePath.transition().duration(300).style("opacity", 0);

    baselinePath.transition().duration(300).style("opacity", 0);

    const anomalyAreaGen = d3.area()
      .x(d => xScale(d.date))
      .y0(yScale(0))
      .y1(d => yScale(d.smoothed_anomaly))
      .defined(d => d.smoothed_anomaly != null)
      .curve(d3.curveMonotoneX);

    const anomalyLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.smoothed_anomaly))
      .defined(d => d.smoothed_anomaly != null)
      .curve(d3.curveMonotoneX);

    zeroLinePath
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", yScale(0))
      .attr("y2", yScale(0))
      .transition()
      .duration(500)
      .style("opacity", 0.45);

    hotAreaPath
      .datum(recentAnomaly.map(d => ({ ...d, smoothed_anomaly: Math.max(d.smoothed_anomaly ?? 0, 0) })))
      .attr("d", anomalyAreaGen)
      .transition()
      .duration(700)
      .style("opacity", 0.32);

    coolAreaPath
      .datum(recentAnomaly.map(d => ({ ...d, smoothed_anomaly: Math.min(d.smoothed_anomaly ?? 0, 0) })))
      .attr("d", anomalyAreaGen)
      .transition()
      .duration(700)
      .style("opacity", 0.2);

    actualLinePath
      .datum(recentAnomaly)
      .attr("stroke", "#ff6b47")
      .attr("d", anomalyLineGen)
      .transition()
      .duration(700)
      .style("opacity", 0.95);

    const positiveShare = recentDaily.length
      ? Math.round((recentDaily.filter(d => d.actual_anomaly > 0).length / recentDaily.length) * 100)
      : 0;

    annotationGroup.append("text")
      .attr("x", width)
      .attr("y", 14)
      .attr("text-anchor", "end")
      .attr("fill", "#ff6b47")
      .style("font-size", "11px")
      .style("font-weight", "700")
      .text(`${positiveShare}% of days above normal`);
  }

  function showToday(lastEntry) {
    if (!lastEntry) return;

    const x = xScale(lastEntry.date);
    const y = yScale(lastEntry.actual);

    annotationGroup.selectAll("*").remove();

    annotationGroup.append("line")
      .attr("x1", x).attr("x2", x)
      .attr("y1", 0).attr("y2", height)
      .attr("stroke", "#f2ede6")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3")
      .style("opacity", 0.35);

    annotationGroup.append("circle")
      .attr("cx", x).attr("cy", y).attr("r", 5)
      .attr("fill", lastEntry.actual_anomaly >= 0 ? "#ff4d1c" : "#4a7fa5")
      .attr("stroke", "#f2ede6").attr("stroke-width", 1.5);

    const anchor = x > width * 0.7 ? "end" : "start";
    const labelX = x > width * 0.7 ? x - 10 : x + 10;
    annotationGroup.append("text")
      .attr("x", labelX).attr("y", y - 10)
      .attr("text-anchor", anchor)
      .attr("fill", "#f2ede6")
      .style("font-size", "12px").style("font-weight", "700")
      .text(`${lastEntry.actual?.toFixed(1)}°${unit}`);
  }

  function showBaselineComparison({ baselineShift } = {}) {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const currentYear = today.getUTCFullYear();

    // Project 1951-1980 baseline onto current year (full year)
    const mmddSeen = new Set();
    const seasonalData = dailyData
      .filter(d => {
        const mmdd = d.date.toISOString().slice(5, 10);
        if (mmddSeen.has(mmdd) || d.baseline_avg == null) return false;
        mmddSeen.add(mmdd);
        return true;
      })
      .map(d => ({
        date: new Date(`${currentYear}-${d.date.toISOString().slice(5, 10)}T00:00:00Z`),
        baseline_avg: d.baseline_avg,
      }))
      .sort((a, b) => a.date - b.date);

    const recentBaselineProjected = recentBaselineData
      .map(row => ({
        date: new Date(`${currentYear}-${row.month_day}T00:00:00Z`),
        recent_avg: row.recent_avg,
      }))
      .sort((a, b) => a.date - b.date);

    const crossoverDate = baselineShift?.isShifted ? (baselineShift?.crossoverDate ?? null) : null;
    const domainCrossover = crossoverDate ?? null;
    const focusStart = new Date(Math.min(today, domainCrossover ?? today));
    focusStart.setUTCDate(focusStart.getUTCDate() - 60);
    const focusEnd = new Date(Math.max(today, domainCrossover ?? today));
    focusEnd.setUTCDate(focusEnd.getUTCDate() + 60);
    const yearStart = new Date(`${currentYear}-01-01T00:00:00Z`);
    const yearEnd = new Date(`${currentYear}-12-31T00:00:00Z`);
    const domainStart = focusStart < yearStart ? yearStart : focusStart;
    const domainEnd = focusEnd > yearEnd ? yearEnd : focusEnd;

    xScale = d3.scaleUtc().domain([domainStart, domainEnd]).range([0, width]);

    xAxisGroup.transition().duration(700)
      .call(
        d3.axisBottom(xScale)
          .ticks(d3.utcMonth.every(1))
          .tickFormat(d3.utcFormat("%b"))
          .tickSizeOuter(0)
      )
      .call(styleAxis);

    // Include current-year smoothed values so the y-scale is already sized
    // for what appears in the next step (THIS YEAR), keeping the axis stable.
    const currentYearData = dailyData.filter(d => d.date.getUTCFullYear() === currentYear && d.date <= today);
    const currentYearSmoothed = createRollingSeries(
      currentYearData.filter(d => d.actual != null),
      d => d.actual,
      30,
      "smoothed_actual",
      "trailing"
    );

    updateYScale([
      ...seasonalData.filter(d => d.date >= domainStart && d.date <= domainEnd).map(d => d.baseline_avg),
      ...recentBaselineProjected.filter(d => d.date >= domainStart && d.date <= domainEnd).map(d => d.recent_avg),
      ...currentYearSmoothed
        .filter(d => d.smoothed_actual != null && d.date >= domainStart && d.date <= today)
        .map(d => d.smoothed_actual),
    ]);
    setYAxisLabel(`Temperature (°${unit})`);
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();

    hotAreaPath.transition().duration(300).style("opacity", 0);
    coolAreaPath.transition().duration(300).style("opacity", 0);
    actualLinePath.transition().duration(300).style("opacity", 0);
    zeroLinePath.transition().duration(300).style("opacity", 0);
    currentYearLinePath.transition().duration(300).style("opacity", 0);
    hideScatterDots();
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();

    const seasonLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.baseline_avg))
      .defined(d => d.baseline_avg != null && d.date >= domainStart && d.date <= domainEnd)
      .curve(d3.curveMonotoneX);

    baselinePath
      .datum(seasonalData)
      .attr("d", seasonLineGen)
      .transition().duration(600)
      .style("opacity", 0.7)
      .attr("stroke-dasharray", "4,3");

    const recentLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.recent_avg))
      .defined(d => d.recent_avg != null && d.date >= domainStart && d.date <= domainEnd && d.date <= today)
      .curve(d3.curveMonotoneX);

    recentBaselinePath
      .datum(recentBaselineProjected)
      .attr("d", recentLineGen)
      .transition().duration(600)
      .style("opacity", 0.85)
      .attr("stroke-dasharray", null);

    // Inline line labels — anchor to today (both lines are visible here)
    const labelDate = new Date(today);
    labelDate.setUTCDate(labelDate.getUTCDate() - 14);
    const labelX = xScale(labelDate);

    function interpY(data, valueKey) {
      for (let i = 0; i < data.length - 1; i++) {
        if (data[i].date <= labelDate && data[i + 1].date >= labelDate) {
          const t = (labelDate - data[i].date) / (data[i + 1].date - data[i].date);
          return data[i][valueKey] + t * (data[i + 1][valueKey] - data[i][valueKey]);
        }
      }
      // fallback: last point
      const last = data.filter(d => d[valueKey] != null).slice(-1)[0];
      return last?.[valueKey] ?? null;
    }

    const baselineLabelY = interpY(seasonalData, "baseline_avg");
    const recentLabelY = interpY(recentBaselineProjected, "recent_avg");
    const minGapPx = 26;

    const FONT2 = "Helvetica Neue, Arial, sans-serif";
    const fs2 = width < 300 ? 10 : 12;
    const OFFSET2 = 36;

    const addCallout2 = (data, key, label, color, isAbove, delay) => {
      const mid = data[Math.floor(data.length / 3)];
      if (!mid) return;
      const ax = xScale(mid.date);
      const ay = yScale(mid[key]);
      const lx = ax - 40;
      const ly = isAbove ? ay - OFFSET2 : ay + OFFSET2;
      const curveEndY = isAbove ? ly + fs2 + 4 : ly - 4;
      const path = `M${ax},${ay} C${ax},${curveEndY} ${lx},${ay} ${lx},${curveEndY}`;
      const g = annotationGroup.append("g").style("opacity", 0);
      g.append("path")
        .attr("d", path).attr("fill", "none")
        .attr("stroke", color).attr("stroke-width", 1)
        .style("opacity", 0.45).attr("stroke-dasharray", "3,2");
      g.append("text")
        .attr("x", lx).attr("y", ly)
        .attr("text-anchor", "middle").attr("fill", color)
        .style("font-size", `${fs2}px`).style("font-weight", "700")
        .style("font-family", FONT2).text(label);
      g.transition().delay(delay).duration(400).style("opacity", 1);
    };

    const addCallout2Fixed = (data, key, label, color, idxFraction, labelX, delay) => {
      const arr = data.filter(d => d[key] != null && d.date >= domainStart && d.date <= domainEnd);
      const mid = arr[Math.floor(arr.length * idxFraction)];
      if (!mid) return;
      const ax = xScale(mid.date);
      const ay = yScale(mid[key]);
      const ly = ay + OFFSET2;
      const curveEndY = ly - 4;
      // Quadratic bezier: control point is midway horizontally, pulled down
      const cpx = (ax + labelX) / 2;
      const cpy = ay + OFFSET2 * 0.8;
      const path = `M${ax},${ay} Q${cpx},${cpy} ${labelX},${curveEndY}`;
      const g = annotationGroup.append("g").style("opacity", 0);
      g.append("path")
        .attr("d", path).attr("fill", "none")
        .attr("stroke", color).attr("stroke-width", 1)
        .style("opacity", 0.45).attr("stroke-dasharray", "3,2");
      g.append("text")
        .attr("x", labelX).attr("y", ly)
        .attr("text-anchor", "middle").attr("fill", color)
        .style("font-size", `${fs2}px`).style("font-weight", "700")
        .style("font-family", FONT2).text(label);
      g.transition().delay(delay).duration(400).style("opacity", 1);
    };

    // 2006-25: bottom-left, 1951-80: bottom-right
    if (recentLabelY != null)
      addCallout2Fixed(recentBaselineProjected.filter(d => d.date <= today), "recent_avg", "2006–25 avg", "#5b9ec9", 0.25, width * 0.2, 400);
    if (baselineLabelY != null)
      addCallout2Fixed(seasonalData, "baseline_avg", "1951–80 avg", "#8b7355", 0.75, width * 0.8, 600);

    // Horizontal reference at today's 2006-25 value
    if (baselineShift?.recentTodayTemp != null) {
      const yRef = yScale(baselineShift.recentTodayTemp);
      annotationGroup.append("line")
        .attr("x1", 0).attr("x2", width)
        .attr("y1", yRef).attr("y2", yRef)
        .attr("stroke", "#5b9ec9").attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,4").style("opacity", 0.4);

      annotationGroup.append("text")
        .attr("x", 4).attr("y", yRef - 5)
        .attr("fill", "#5b9ec9")
        .style("font-size", "10px")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(`2006–25 avg today: ${baselineShift.recentTodayTemp.toFixed(1)}°${unit}`);
    }

    // Structural shift annotation
    if (baselineShift?.crossoverDate && baselineShift?.isShifted) {
      const todayX = xScale(today);
      const crossoverX = xScale(baselineShift.crossoverDate);
      const isEarlier = baselineShift.isEarlier;
      const label = isEarlier
        ? `${baselineShift.shiftDays} days earlier`
        : `${baselineShift.shiftDays} days later`;

      annotationGroup.append("line")
        .attr("x1", todayX).attr("x2", todayX)
        .attr("y1", 0).attr("y2", height)
        .attr("stroke", "#f2ede6").attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,3").style("opacity", 0.4);

      annotationGroup.append("text")
        .attr("x", todayX - 5).attr("y", height - 8)
        .attr("text-anchor", "end").attr("fill", "#a89880")
        .style("font-size", "10px")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text("Today");

      annotationGroup.append("line")
        .attr("x1", crossoverX).attr("x2", crossoverX)
        .attr("y1", 0).attr("y2", height)
        .attr("stroke", "#8b7355").attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,3").style("opacity", 0.7);

      const cAnchor = crossoverX > width * 0.72 ? "end" : "start";
      const cLx = crossoverX > width * 0.72 ? crossoverX - 5 : crossoverX + 5;
      annotationGroup.append("text")
        .attr("x", cLx).attr("y", height - 8)
        .attr("text-anchor", cAnchor).attr("fill", "#8b7355")
        .style("font-size", "10px")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(baselineShift.crossoverMonthStr);

      // Shade the gap between today and crossover
      const gapX = Math.min(todayX, crossoverX);
      const gapW = Math.abs(crossoverX - todayX);
      annotationGroup.append("rect")
        .attr("x", gapX).attr("width", gapW)
        .attr("y", 0).attr("height", height)
        .attr("fill", "#ff4d1c").style("opacity", 0.07);

      const midX = (todayX + crossoverX) / 2;
      const yRef = baselineShift.recentTodayTemp != null ? yScale(baselineShift.recentTodayTemp) : 20;
      annotationGroup.append("text")
        .attr("x", midX).attr("y", yRef - 18)
        .attr("text-anchor", "middle").attr("fill", "#ff6b47")
        .style("font-size", "10px").style("font-weight", "700")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(label);
    }
  }

  function showSeasonalShift({ avg30, crossoverDate, isEarlier = false, isLater = false, exceedsBaseline = false, baselineMax = null, baselinePeakDate = null }) {
    if (avg30 == null) return;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const currentYear = today.getUTCFullYear();

    // 1951-1980 baseline: one entry per MM-DD projected onto current year
    const mmddSeen = new Set();
    const seasonalData = dailyData
      .filter(d => {
        const mmdd = d.date.toISOString().slice(5, 10);
        if (mmddSeen.has(mmdd) || d.baseline_avg == null) return false;
        mmddSeen.add(mmdd);
        return true;
      })
      .map(d => ({
        date: new Date(`${currentYear}-${d.date.toISOString().slice(5, 10)}T00:00:00Z`),
        baseline_avg: d.baseline_avg,
      }))
      .sort((a, b) => a.date - b.date);

    const recentBaselineProjected = recentBaselineData
      .map(row => ({
        date: new Date(`${currentYear}-${row.month_day}T00:00:00Z`),
        recent_avg: row.recent_avg,
      }))
      .sort((a, b) => a.date - b.date);

    const shiftDays = crossoverDate ? Math.abs(Math.round((crossoverDate - today) / (1000 * 60 * 60 * 24))) : null;
    const effectiveCrossover = crossoverDate ?? null;

    const focusStart = new Date(Math.min(today, effectiveCrossover ?? today));
    focusStart.setUTCDate(focusStart.getUTCDate() - 60);

    const focusEnd = new Date(Math.max(today, effectiveCrossover ?? today));
    focusEnd.setUTCDate(focusEnd.getUTCDate() + 60);

    const yearStart = new Date(`${currentYear}-01-01T00:00:00Z`);
    const yearEnd = new Date(`${currentYear}-12-31T00:00:00Z`);
    const domainStart = focusStart < yearStart ? yearStart : focusStart;
    const domainEnd = focusEnd > yearEnd ? yearEnd : focusEnd;

    xScale = d3.scaleUtc()
      .domain([domainStart, domainEnd])
      .range([0, width]);

    xAxisGroup.transition().duration(700)
      .call(
        d3.axisBottom(xScale)
          .ticks(d3.utcMonth.every(1))
          .tickFormat(d3.utcFormat("%b"))
          .tickSizeOuter(0)
      )
      .call(styleAxis);

    // Y-scale inherited from showBaselineComparison — don't re-animate it.
    setYAxisLabel(`Temperature (°${unit})`);
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();

    hotAreaPath.transition().duration(300).style("opacity", 0);
    coolAreaPath.transition().duration(300).style("opacity", 0);
    actualLinePath.transition().duration(300).style("opacity", 0);
    zeroLinePath.transition().duration(300).style("opacity", 0);
    hideScatterDots();
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();

    // 2006-25 baseline — redraw for new domain, then fade to context-only opacity
    const recentLineGen2 = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.recent_avg))
      .defined(d => d.recent_avg != null && d.date >= domainStart && d.date <= domainEnd && d.date <= today)
      .curve(d3.curveMonotoneX);

    recentBaselinePath
      .datum(recentBaselineProjected)
      .attr("d", recentLineGen2)
      .transition().duration(600).style("opacity", 0.12);

    // 1951-80 baseline stays visible — this year is compared against it
    const seasonLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.baseline_avg))
      .defined(d => d.baseline_avg != null && d.date >= domainStart && d.date <= domainEnd)
      .curve(d3.curveMonotoneX);

    baselinePath
      .datum(seasonalData)
      .attr("d", seasonLineGen)
      .transition().duration(600)
      .style("opacity", 0.55)
      .attr("stroke-dasharray", "4,3");

    // Current year 30-day rolling avg fades in
    const currentYearData = dailyData.filter(d => d.date.getUTCFullYear() === currentYear && d.date <= today);
    const currentYearSmoothed = createRollingSeries(
      currentYearData.filter(d => d.actual != null),
      d => d.actual,
      30,
      "smoothed_actual",
      "trailing"
    );

    const cyLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.smoothed_actual))
      .defined(d => d.smoothed_actual != null && d.date >= domainStart && d.date <= domainEnd && d.date <= today)
      .curve(d3.curveMonotoneX);

    currentYearLinePath
      .datum(currentYearSmoothed)
      .attr("stroke", "#ff6b47")
      .attr("stroke-width", 2.25)
      .attr("d", cyLineGen)
      .transition().duration(600)
      .style("opacity", 0.85);

    // Inline labels near today
    const labelDate = new Date(today);
    labelDate.setUTCDate(labelDate.getUTCDate() - 10);
    const labelX = xScale(labelDate);
    function interpYLine(data, valueKey) {
      for (let i = 0; i < data.length - 1; i++) {
        if (data[i].date <= labelDate && data[i + 1].date >= labelDate) {
          const t = (labelDate - data[i].date) / (data[i + 1].date - data[i].date);
          return data[i][valueKey] + t * (data[i + 1][valueKey] - data[i][valueKey]);
        }
      }
      return data[data.length - 1]?.[valueKey] ?? null;
    }
    const baselineLabelY = interpYLine(seasonalData, "baseline_avg");
    const cyLabelY = interpYLine(currentYearSmoothed.filter(d => d.smoothed_actual != null), "smoothed_actual");
    const minGap = 26;
    const baselinePx = baselineLabelY != null ? yScale(baselineLabelY) + 4 : null;
    const cyRawPx = cyLabelY != null ? yScale(cyLabelY) - 4 : null;
    const cyAdjustedPx = cyRawPx != null && baselinePx != null && Math.abs(cyRawPx - baselinePx) < minGap
      ? (cyRawPx < baselinePx ? baselinePx - minGap : baselinePx + minGap)
      : cyRawPx;

    const FONT3 = "Helvetica Neue, Arial, sans-serif";
    const fs3 = width < 300 ? 10 : 12;
    const OFFSET3 = 36;

    const addCallout3 = (data, key, label, color, idxFraction, lx, delay) => {
      const arr = data.filter(d => d[key] != null && d.date >= domainStart && d.date <= domainEnd);
      const mid = arr[Math.floor(arr.length * idxFraction)];
      if (!mid) return;
      const ax = xScale(mid.date);
      const ay = yScale(mid[key]);
      const ly = ay + OFFSET3;
      const curveEndY = ly - 4;
      const cpx = (ax + lx) / 2;
      const cpy = ay + OFFSET3 * 0.8;
      const path = `M${ax},${ay} Q${cpx},${cpy} ${lx},${curveEndY}`;
      const g = annotationGroup.append("g").style("opacity", 0);
      g.append("path")
        .attr("d", path).attr("fill", "none")
        .attr("stroke", color).attr("stroke-width", 1)
        .style("opacity", 0.45).attr("stroke-dasharray", "3,2");
      g.append("text")
        .attr("x", lx).attr("y", ly)
        .attr("text-anchor", "middle").attr("fill", color)
        .style("font-size", `${fs3}px`).style("font-weight", "700")
        .style("font-family", FONT3).text(label);
      g.transition().delay(delay).duration(400).style("opacity", 1);
    };

    // "This year" bottom-left, "1951-80" bottom-right
    addCallout3(currentYearSmoothed, "smoothed_actual", "This year", "#ff6b47", 0.25, width * 0.2, 400);
    addCallout3(seasonalData, "baseline_avg", "1951–80 avg", "#8b7355", 0.75, width * 0.8, 600);

    const yRef = yScale(avg30);
    const todayX = xScale(today);

    // Horizontal reference line at 30-day avg
    annotationGroup.append("line")
      .attr("x1", 0).attr("x2", width)
      .attr("y1", yRef).attr("y2", yRef)
      .attr("stroke", "#f2ede6").attr("stroke-width", 1)
      .attr("stroke-dasharray", "5,4")
      .style("opacity", 0.4);

    annotationGroup.append("text")
      .attr("x", 4).attr("y", yRef - 5)
      .attr("fill", "#a89880")
      .style("font-size", "10px")
      .style("font-family", "Helvetica Neue, Arial, sans-serif")
      .text(`Recent 30-day avg: ${avg30.toFixed(1)}°${unit}`);

    // Today vertical line
    annotationGroup.append("line")
      .attr("x1", todayX).attr("x2", todayX)
      .attr("y1", 0).attr("y2", height)
      .attr("stroke", "#f2ede6").attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3")
      .style("opacity", 0.4);

    annotationGroup.append("text")
      .attr("x", todayX - 5).attr("y", height - 8)
      .attr("text-anchor", "end")
      .attr("fill", "#a89880")
      .style("font-size", "10px")
      .style("font-family", "Helvetica Neue, Arial, sans-serif")
      .text("Today");

    // Crossover line + shaded gap — only if within meaningful range
    if (effectiveCrossover) {
      const crossoverX = xScale(effectiveCrossover);
      // Warm spring: the old normal reaches these temperatures after today
      // (earlier). Warm autumn: it last had them before today (later).
      const isWarmShift = isEarlier || isLater;

      if (isWarmShift) {
        annotationGroup.append("rect")
          .attr("x", Math.min(todayX, crossoverX)).attr("width", Math.abs(crossoverX - todayX))
          .attr("y", 0).attr("height", height)
          .attr("fill", "#ff4d1c").style("opacity", 0.07);
      }

      annotationGroup.append("line")
        .attr("x1", crossoverX).attr("x2", crossoverX)
        .attr("y1", 0).attr("y2", height)
        .attr("stroke", "#8b7355").attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,3")
        .style("opacity", 0.7);

      const anchor = crossoverX > width * 0.72 ? "end" : "start";
      const lx = crossoverX > width * 0.72 ? crossoverX - 5 : crossoverX + 5;

      annotationGroup.append("text")
        .attr("x", lx).attr("y", height - 8)
        .attr("text-anchor", anchor)
        .attr("fill", "#8b7355")
        .style("font-size", "10px")
        .style("font-family", "Helvetica Neue, Arial, sans-serif")
        .text(effectiveCrossover.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }));

      // Gap label midpoint
      if (isWarmShift) {
        const midX = (todayX + crossoverX) / 2;
        annotationGroup.append("text")
          .attr("x", midX).attr("y", yRef - 18)
          .attr("text-anchor", "middle")
          .attr("fill", "#ff6b47")
          .style("font-size", "10px")
          .style("font-weight", "700")
          .style("font-family", "Helvetica Neue, Arial, sans-serif")
          .text(`${shiftDays} days ${isEarlier ? "earlier" : "later"}`);
      }
    }

    // Peak annotation when avg30 exceeds the entire 1951-80 baseline range
    if (exceedsBaseline && baselinePeakDate && baselineMax != null) {
      const peakX = xScale(baselinePeakDate);
      const peakY = yScale(baselineMax);
      const FONT = "Helvetica Neue, Arial, sans-serif";

      if (peakX >= 0 && peakX <= width) {
        // Vertical tick at peak
        annotationGroup.append("line")
          .attr("x1", peakX).attr("x2", peakX)
          .attr("y1", peakY - 8).attr("y2", peakY + 8)
          .attr("stroke", "#8b7355").attr("stroke-width", 1.5)
          .style("opacity", 0.8);

        const anchor = peakX > width * 0.65 ? "end" : "start";
        const lx = peakX > width * 0.65 ? peakX - 6 : peakX + 6;

        // Peak label
        annotationGroup.append("text")
          .attr("x", lx).attr("y", peakY - 12)
          .attr("text-anchor", anchor)
          .attr("fill", "#8b7355")
          .style("font-size", "9px").style("font-family", FONT)
          .text("Historical peak");

        annotationGroup.append("text")
          .attr("x", lx).attr("y", peakY + 20)
          .attr("text-anchor", anchor)
          .attr("fill", "#8b7355")
          .style("font-size", "11px").style("font-weight", "700").style("font-family", FONT)
          .text(`${baselineMax.toFixed(1)}°${unit}`);
      }
    }

    annotationGroup.append("text")
      .attr("x", width)
      .attr("y", 14)
      .attr("text-anchor", "end")
      .attr("fill", "#a89880")
      .style("font-size", "11px")
      .style("font-weight", "700")
      .text("30-day average vs. seasonal norm");
  }

  function set100DayAxis() {
    xAxisGroup.transition().duration(700)
      .call(d3.axisBottom(xScale).ticks(d3.utcMonth.every(1)).tickFormat(d3.utcFormat("%b")).tickSizeOuter(0))
      .call(styleAxis);
  }

  function show100DayBaseline() {
    xScale = xScale100.copy();
    set100DayAxis();

    updateYScale(_100DayYValues);
    setYAxisLabel(`Temperature (°${unit})`);

    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hideAreaElements();
    hideScatterDots();
    periodRect.transition().duration(400).style("opacity", 0);
    annotationGroup.selectAll("*").remove();

    // duration 0 avoids string-interpolating the path between two different
    // scale spaces, which produces a nonsensical shape mid-transition
    updateBaselineCurve(last100Daily, 0);
    baselinePath.transition().duration(700).style("opacity", 0.85);
  }

  function show100DayRawTemps() {
    xScale = xScale100.copy();
    set100DayAxis();

    const absValues = last100Daily.flatMap(d => [d.actual, d.baseline_avg]).filter(v => v != null);
    updateYScale(absValues);
    setYAxisLabel(`Anomaly vs. 1951–1980 baseline (°${unit})`);

    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hideAreaElements();
    hideScatterDots();
    annotationGroup.selectAll("*").remove();

    updateBaselineCurve(last100Daily, 0);
    baselinePath.transition().duration(400).style("opacity", 0.65);
    renderBarsRaw(last100Daily);
  }

  function show100DayTwoBaselines() {
    xScale = xScale100.copy();
    set100DayAxis();

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const currentYear = today.getUTCFullYear();

    // Project recent baseline onto the last 100 calendar days.
    // MM-DDs that fall before Jan 1 of current year belong to the previous year.
    const recentProjected = recentBaselineData
      .map(row => {
        const cyDate = new Date(`${currentYear}-${row.month_day}T00:00:00Z`);
        const date = cyDate > today
          ? new Date(`${currentYear - 1}-${row.month_day}T00:00:00Z`)
          : cyDate;
        return { date, recent_avg: row.recent_avg };
      })
      .filter(d => d.recent_avg != null && d.date >= _window100Start && d.date <= today)
      .sort((a, b) => a.date - b.date);

    updateYScale(_100DayYValues);
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hideAreaElements();
    hideScatterDots();
    actualLinePath.transition().duration(300).style("opacity", 0);
    zeroLinePath.transition().duration(300).style("opacity", 0);
    annotationGroup.selectAll("*").remove();

    // 1951-80 baseline (dashed, same as previous step)
    updateBaselineCurve(last100Daily, 0);
    baselinePath.transition().duration(400).style("opacity", 0.65);

    // 2006-25 recent baseline
    const recentLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.recent_avg))
      .defined(d => d.recent_avg != null)
      .curve(d3.curveMonotoneX);

    recentBaselinePath
      .datum(recentProjected)
      .attr("d", recentLineGen)
      .transition().duration(600)
      .style("opacity", 0.8);

    // No fill between the lines — just the two curves.
    hotAreaPath.transition().duration(300).style("opacity", 0);

    // Inline labels
    const FONT = "Helvetica Neue, Arial, sans-serif";
    const labelDate = new Date(today);
    labelDate.setUTCDate(labelDate.getUTCDate() - 14);
    const labelX = xScale(labelDate);

    const interpY = (data, key) => {
      const i = d3.bisectLeft(data.map(d => d.date), labelDate);
      const a = data[Math.max(0, i - 1)];
      const b = data[Math.min(data.length - 1, i)];
      if (!a || !b) return null;
      const t = (labelDate - a.date) / (b.date - a.date || 1);
      return yScale(a[key] + t * (b[key] - a[key]));
    };

    const baselineY = interpY(last100Daily, "baseline_avg");
    const recentY = interpY(recentProjected, "recent_avg");

    // Curved callout labels — arcs from line to nearby label
    const fs = width < 300 ? 10 : 12;
    const OFFSET = 36; // how far above/below the line the label sits

    const addCallout = (data, key, label, color, isAbove, delay) => {
      const mid = data[Math.floor(data.length / 3)]; // attach near the left third
      if (!mid) return;
      const ax = xScale(mid.date);
      const ay = yScale(mid[key]);
      const lx = ax - 40; // label left of attachment point
      const ly = isAbove ? ay - OFFSET : ay + OFFSET;
      // curve ends a few px before the label text
      const curveEndY = isAbove ? ly + fs + 4 : ly - 4;

      const g = annotationGroup.append("g").style("opacity", 0);

      // Cubic bezier: start on line, curve toward label
      const path = `M${ax},${ay} C${ax},${curveEndY} ${lx},${ay} ${lx},${curveEndY}`;
      g.append("path")
        .attr("d", path)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 1)
        .style("opacity", 0.45)
        .attr("stroke-dasharray", "3,2");


      // Label
      g.append("text")
        .attr("x", lx)
        .attr("y", ly)
        .attr("text-anchor", "middle")
        .attr("fill", color)
        .style("font-size", `${fs}px`)
        .style("font-weight", "700")
        .style("font-family", FONT)
        .text(label);

      g.transition().delay(delay).duration(400).style("opacity", 1);
    };

    addCallout(recentProjected, "recent_avg", "2006–25 avg", "#5b9ec9", true, 400);
    addCallout(last100Daily, "baseline_avg", "1951–80 avg", "#8b7355", false, 600);
  }

  function show100DayDifference() {
    xScale = xScale100.copy();
    set100DayAxis();

    // Extra padding so hottest/coldest annotation labels don't overlap the lines
    const [vMin, vMax] = d3.extent(_100DayYValues.filter(v => v != null));
    const pad = Math.max((vMax - vMin) * 0.18, 2);
    yScale.domain([vMin - pad, vMax + pad]).nice();
    yAxisGroup.transition().duration(600).call(yAxisFn).call(styleAxis);
    chartArea.selectAll(".bar").transition().duration(400).style("opacity", 0).remove();
    hideAreaElements();
    hideScatterDots();
    annotationGroup.selectAll("*").remove();
    zeroLinePath.transition().duration(300).style("opacity", 0);

    // Baseline curve stays from previous step
    updateBaselineCurve(last100Daily, 0);
    baselinePath.transition().duration(400).style("opacity", 0.65);

    // Actual temperature line
    const actualLineGen = d3.line()
      .x(d => xScale(d.date))
      .y(d => yScale(d.actual))
      .defined(d => d.actual != null && d.baseline_avg != null)
      .curve(d3.curveMonotoneX);

    actualLinePath
      .datum(last100Daily)
      .attr("d", actualLineGen)
      .transition().duration(600)
      .style("opacity", 0.75);

    // Use clip paths so fills are continuous across crossover points (no gaps).
    // Both areas draw the same full path; clipping handles above/below separation.
    svg.selectAll(`#hot-clip-${containerId}, #cool-clip-${containerId}`).remove();
    const defs = svg.select("defs");

    const aboveClipArea = d3.area()
      .x(d => xScale(d.date))
      .y0(-10)
      .y1(d => yScale(d.baseline_avg))
      .defined(d => d.baseline_avg != null)
      .curve(d3.curveMonotoneX);

    defs.append("clipPath")
      .attr("id", `hot-clip-${containerId}`)
      .append("path")
      .attr("d", aboveClipArea(last100Daily));

    const belowClipArea = d3.area()
      .x(d => xScale(d.date))
      .y0(d => yScale(d.baseline_avg))
      .y1(height + 10)
      .defined(d => d.baseline_avg != null)
      .curve(d3.curveMonotoneX);

    defs.append("clipPath")
      .attr("id", `cool-clip-${containerId}`)
      .append("path")
      .attr("d", belowClipArea(last100Daily));

    const fullAreaGen = d3.area()
      .x(d => xScale(d.date))
      .y0(d => yScale(d.baseline_avg))
      .y1(d => yScale(d.actual))
      .defined(d => d.actual != null && d.baseline_avg != null)
      .curve(d3.curveMonotoneX);

    hotAreaPath
      .datum(last100Daily)
      .attr("d", fullAreaGen)
      .attr("clip-path", `url(#hot-clip-${containerId})`)
      .attr("fill", "#ff4d1c")
      .transition().duration(600)
      .style("opacity", 0.35);

    coolAreaPath
      .datum(last100Daily)
      .attr("d", fullAreaGen)
      .attr("clip-path", `url(#cool-clip-${containerId})`)
      .attr("fill", "#4a7fa5")
      .transition().duration(600)
      .style("opacity", 0.35);

    // Annotate the single hottest and coldest anomaly day
    const annotatable = last100Daily.filter(d => d.actual != null && d.actual_anomaly != null);
    if (annotatable.length) {
      const hotDay = annotatable.reduce((a, b) => b.actual_anomaly > a.actual_anomaly ? b : a);
      const coldDay = annotatable.reduce((a, b) => b.actual_anomaly < a.actual_anomaly ? b : a);

      const FONT = "Helvetica Neue, Arial, sans-serif";
      const LEADER = width < 300 ? 28 : 44; // shorter leader on mobile

      [[hotDay, "#ff6b47", "above"], [coldDay, "#5b9ec9", "below"]].forEach(([d, color, side]) => {
        const cx = xScale(d.date);
        const cy = yScale(d.actual);
        const isAbove = side === "above";
        const lyRaw = isAbove ? cy - LEADER : cy + LEADER;
        // Clamp so label stays within chart vertically
        const ly = isAbove
          ? Math.max(32, lyRaw)
          : Math.min(height - 32, lyRaw);

        const tempStr = `${d.actual.toFixed(0)}°${unit}`;
        const dateStr = d.date.toLocaleDateString("en-US", {
          month: "short", day: "numeric", year: "numeric", timeZone: "UTC"
        });

        // Clamp label x so it stays within chart bounds
        const lx = Math.max(44, Math.min(width - 44, cx));
        const anchor = lx < cx - 4 ? "end" : lx > cx + 4 ? "start" : "middle";

        const ann = annotationGroup.append("g").style("opacity", 0);

        // Dot on actual line
        ann.append("circle")
          .attr("cx", cx).attr("cy", cy)
          .attr("r", 3.5)
          .attr("fill", color)
          .style("opacity", 0.9);

        // Bezier leader: starts at dot, curves to label
        // Control point is horizontally midway and vertically near the label end
        const lineStartY = isAbove ? cy - 4 : cy + 4;
        const lineEndY = isAbove ? ly : ly;
        const cpx = (cx + lx) / 2;
        const cpy = isAbove ? lineStartY - Math.abs(lineStartY - lineEndY) * 0.4
                            : lineStartY + Math.abs(lineStartY - lineEndY) * 0.4;
        const leaderPath = `M${cx},${lineStartY} Q${cpx},${cpy} ${lx},${lineEndY}`;
        ann.append("path")
          .attr("d", leaderPath)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 1)
          .style("opacity", 0.45);

        // Temperature (big)
        ann.append("text")
          .attr("x", lx)
          .attr("y", isAbove ? ly - 16 : ly + 14)
          .attr("text-anchor", anchor)
          .attr("fill", color)
          .style("font-size", "17px")
          .style("font-family", FONT)
          .style("font-weight", "700")
          .text(tempStr);

        // Date (smaller, below temp)
        ann.append("text")
          .attr("x", lx)
          .attr("y", isAbove ? ly - 3 : ly + 27)
          .attr("text-anchor", anchor)
          .attr("fill", color)
          .style("font-size", "9px")
          .style("font-family", FONT)
          .style("font-weight", "400")
          .style("opacity", 0.75)
          .text(dateStr);

        ann.transition().delay(700).duration(400).style("opacity", 1);
      });
    }
  }

  // Init
  showIntro();

  return { showIntro, showBaseline, showWarmingRate, showDivergence, showRecent, showToday, showBaselineComparison, showSeasonalShift, show100DayBaseline, show100DayRawTemps, show100DayTwoBaselines, show100DayDifference };
};
